import { useState, useEffect } from 'react';
import { UserIcon } from 'lucide-react';
import { getFollowing, sendInvites } from '@/lib/api';
import type { Profile } from '@/types/database.types';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import Modal from '@/components/Modal';
import Button from '@/components/form/Button';
import { toast } from 'react-hot-toast';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  snapId: string;
  snapTitle: string;
  onMarkChange?: () => void;
}

export default function InviteModal({ isOpen, onClose, snapId, snapTitle, onMarkChange }: InviteModalProps) {
  const { user } = useAuth();
  const [following, setFollowing] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [inviteStatuses, setInviteStatuses] = useState<Map<string, string>>(new Map());

  const DEFAULT_AVATAR = 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png?20150327203541';

  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !user) return;
      
      try {
        setLoading(true);
        
        // Get following users
        const response = await getFollowing(user.id, 1, 100);
        
        // Get existing invites for this SNAP
        const { data: invites } = await supabase
          .from('snap_invites')
          .select('invited_user_id, status')
          .eq('snap_id', snapId);
        
        // Create map of invited user IDs to their status
        const statusMap = new Map(
          invites?.map(invite => [invite.invited_user_id, invite.status]) || []
        );
        setInviteStatuses(statusMap);
        
        setFollowing(response.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load friends list');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
      setSelectedUsers(new Set());
      setMessage('');
    }
  }, [isOpen, snapId, user]);

  const handleToggleUser = (userId: string) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSendInvites = async () => {
    if (selectedUsers.size === 0) {
      toast.error('Please select at least one user to invite');
      return;
    }

    try {
      setSending(true);
      await sendInvites(snapId, Array.from(selectedUsers), message);
      toast.success('Invitations sent successfully!');
      onMarkChange?.();
      onClose();
    } catch (error) {
      console.error('Error sending invites:', error);
      toast.error('Failed to send invitations');
    } finally {
      setSending(false);
    }
  };

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    setImageError(true);
    event.currentTarget.src = DEFAULT_AVATAR;
  };

  const getInviteStatus = (userId: string) => {
    const status = inviteStatuses.get(userId);
    if (!status) return null;
    
    switch (status) {
      case 'accepted':
        return { text: 'Already accepted', color: 'text-green-600' };
      case 'declined':
        return { text: 'Declined', color: 'text-red-600' };
      case 'pending':
        return { text: 'Pending response', color: 'text-yellow-600' };
      default:
        return { text: 'Already invited', color: 'text-gray-500' };
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Invite Friends"
    >
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : following.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Follow some users to invite them to your SNAPs
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {following.map((user) => {
                const inviteStatus = getInviteStatus(user.id);
                const isInvited = inviteStatuses.has(user.id);
                return (
                  <label
                    key={user.id}
                    className={`flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 ${
                      isInvited ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.username}
                          className="h-10 w-10 rounded-full object-cover"
                          onError={handleImageError}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-gray-500" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-gray-900">{user.username}</div>
                        {inviteStatus && (
                          <div className={`text-sm ${inviteStatus.color}`}>
                            {inviteStatus.text}
                          </div>
                        )}
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.id)}
                      onChange={() => !isInvited && handleToggleUser(user.id)}
                      disabled={isInvited}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                    />
                  </label>
                );
              })}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add a Message (Optional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="Add a personal message to your invitation..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="ghost"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendInvites}
                loading={sending}
                disabled={selectedUsers.size === 0 || sending}
              >
                Send Invites
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}